import {isNumber} from 'lodash';
import {ellipsis} from 'polished';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {defineMessage, Trans, useI18n} from '../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {BoxedInput, StyledBoxDiv} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {buildTeammateDisplayName, inboxHasColor} from '../../helpers/modelHelpers';
import {CircleModel, CirclePublicId, isCirclePublicId} from '../../models/api/circleModel';
import {
  DataFieldsTableColumnModel,
  DataFieldsTableColumnTypesEnum,
} from '../../models/api/dataFieldsTableColumnModel';
import {dataFieldsTableListValueSeparator} from '../../models/api/dataFieldsTableModel';
import {InboxModel} from '../../models/api/inboxModel';
import {TagModel} from '../../models/api/tagModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {buildPublicId, tryParsePublicId} from '../../models/applications/publicIdTypes';
import {isExpression} from '../../settings/editors/expressions/expressionModel';
import {isExpressionRef} from '../../settings/editors/expressions/expressionRefModel';
import {useFetchCircles} from '../../settings/fetchers/circles';
import {InboxesParameter, InboxParameter} from '../../settings/rules/ruleEditor/parameters/inboxParameter';
import {TagParameter, TagsParameter} from '../../settings/rules/ruleEditor/parameters/tagParameter';
import {
  TeammateParameter,
  TeammatesParameter,
} from '../../settings/rules/ruleEditor/parameters/teammateParameter';
import {TeammateAvatar} from '../common/avatars/teammateAvatar';
import {renderDropdownValueItems} from '../common/dropdowns/dropdownHelpers';
import {renderTagIcon} from '../common/dropdowns/dropdownItemTagIcon';
import {DataFieldsTableSearchValue, useDataFieldsTableSearchContext} from './dataFieldsTableSearchContext';
import {
  deserializeDataFieldsTableResourceListValue,
  deserializeDataFieldsTableResourceValue,
  serializeDataFieldsTableResourceListValue,
  serializeDataFieldsTableResourceValue,
} from './helpers';

/*
 * Props.
 */

export interface DataFieldsTableCellProps {
  column: DataFieldsTableColumnModel;
  value: string | undefined;
  onChange: HandlerOf<string | undefined>;
}

/*
 * Styles.
 */

const StyledBoxedInput = styled(BoxedInput)`
  width: 100%;
  ${StyledBoxDiv} {
    background-color: transparent;

    :hover:not(:focus-within) {
      background-color: ${(p) => p.theme.greys.shade20};
    }
  }
`;

const StyledResourceLabelWrapper = styled.div<{$isPlaceholder: boolean}>`
  ${flexCenter};
  padding: 0 5px;

  height: 26px;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};

  color: ${(p) => (p.$isPlaceholder ? p.theme.greys.shade60 : p.theme.greys.shade80)};
  overflow: hidden;
`;

const StyledResourceItemWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
`;

const StyledResourceLabel = styled.div`
  ${ellipsis()};
  line-height: 18px;
`;

/*
 * Component.
 */

export const DataFieldsTableCellRenderer: FC<DataFieldsTableCellProps> = (props) => {
  const CellRenderer = CELL_RENDERERS[props.column.type];
  if (!CellRenderer) {
    reportToBugsnag(new Error(`Missing data fields table renderer for type: ${props.column.type}`));
    return <span>Missing renderer for type: {props.column.type}</span>;
  }
  return <CellRenderer {...props} />;
};

const TextRenderer: FC<DataFieldsTableCellProps> = ({value: initialValue, onChange}) => {
  const {i18n} = useI18n();
  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState(initialValue);

  // If the initialValue is changed externally, sync it up with our state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onBlur = () => {
    if (value !== initialValue) {
      onChange(value);
    }
  };

  return (
    <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL}>
      {(childProps) => (
        <BoxedInputElement
          {...childProps}
          placeholder={i18n._(
            defineMessage({
              id: 'rule-editor-data-table-text-cell-placeholder',
              message: 'Enter data',
            }),
          )}
          onBlur={onBlur}
          onKeyDown={(event) => {
            if (event.key === Key.Enter && !event.shiftKey) {
              onBlur();
            }
          }}
          type={BoxedInputElementTypesEnum.TEXT}
          value={value}
          onChange={(v) => setValue(v)}
        />
      )}
    </StyledBoxedInput>
  );
};

const TagRenderer: FC<DataFieldsTableCellProps> = ({value: encodedValue, onChange}) => {
  const {addSearchData} = useDataFieldsTableSearchContext();
  const tagId = deserializeDataFieldsTableResourceValue(encodedValue, 'tag');

  return (
    <TagParameter
      buttonWidth="100%"
      onFetchSelectedItems={(tags) => addSearchData(tags.map(tagToSearchValue))}
      renderButton={(selectedTag, isOpen) => (
        <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
          {() => (
            <StyledResourceLabelWrapper $isPlaceholder={!selectedTag}>
              {selectedTag && !isExpression(selectedTag) ? (
                renderTagValue(selectedTag)
              ) : (
                <Trans id="rule-editor-data-table-tag-cell-placeholder">Select a tag</Trans>
              )}
            </StyledResourceLabelWrapper>
          )}
        </StyledBoxedInput>
      )}
      selectedItem={tagId ?? null}
      onChange={(value) => {
        if (isExpressionRef(value)) {
          return;
        }
        onChange(value ? buildPublicId('tag', value) : undefined);
      }}
    />
  );
};

const TagListRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => {
  const {addSearchData} = useDataFieldsTableSearchContext();
  const tagIds = deserializeDataFieldsTableResourceListValue(value, 'tag');

  return (
    <TagsParameter
      buttonWidth="100%"
      onFetchSelectedItems={(tags) => addSearchData(tags.map(tagToSearchValue))}
      renderButton={(selectedTagsAndExpressions, isOpen) => {
        const selectedTags = selectedTagsAndExpressions?.filter((tag): tag is TagModel => !isExpression(tag));
        return (
          <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
            {() => (
              <StyledResourceLabelWrapper $isPlaceholder={!selectedTags?.length}>
                {selectedTags?.length ? (
                  renderDropdownValueItems(
                    selectedTags.map((tag) => ({key: String(tag.id), tag})),
                    ({tag}) => renderTagValue(tag),
                    {maxItems: 3},
                  )
                ) : (
                  <Trans id="rule-editor-data-table-tag-list-cell-placeholder">Select tags</Trans>
                )}
              </StyledResourceLabelWrapper>
            )}
          </StyledBoxedInput>
        );
      }}
      selectedItems={tagIds ?? null}
      onChange={(newSelectedTags) => {
        onChange(serializeDataFieldsTableResourceListValue(newSelectedTags?.filter(isNumber), 'tag'));
      }}
    />
  );
};

const InboxRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => {
  const inboxId = deserializeDataFieldsTableResourceValue(value, 'inbox');
  const {addSearchData} = useDataFieldsTableSearchContext();

  return (
    <InboxParameter
      showOtherNamespaceInboxes
      selectedItem={inboxId ?? null}
      buttonWidth="100%"
      onFetchSelectedItems={(inboxes) => addSearchData(inboxes.map(inboxToSearchValue))}
      renderButton={(selectedInbox, isOpen) => (
        <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
          {() => (
            <StyledResourceLabelWrapper $isPlaceholder={!selectedInbox}>
              {selectedInbox && !isExpression(selectedInbox) ? (
                renderInboxValue(selectedInbox)
              ) : (
                <Trans id="rule-editor-data-table-inbox-cell-placeholder">Select an inbox</Trans>
              )}
            </StyledResourceLabelWrapper>
          )}
        </StyledBoxedInput>
      )}
      onChange={(newInboxId) => {
        if (isExpressionRef(newInboxId)) {
          return;
        }
        onChange(serializeDataFieldsTableResourceValue(newInboxId, 'inbox'));
      }}
    />
  );
};

const InboxListRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => {
  const inboxIds = deserializeDataFieldsTableResourceListValue(value, 'inbox');
  const {addSearchData} = useDataFieldsTableSearchContext();

  return (
    <InboxesParameter
      showOtherNamespaceInboxes
      buttonWidth="100%"
      onFetchSelectedItems={(inboxes) => addSearchData(inboxes.map(inboxToSearchValue))}
      renderButton={(selectedInboxesAndExpressions, isOpen) => {
        const selectedInboxes = selectedInboxesAndExpressions?.filter(
          (inbox): inbox is InboxModel => !isExpression(inbox),
        );
        return (
          <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
            {() => (
              <StyledResourceLabelWrapper $isPlaceholder={!selectedInboxes?.length}>
                {selectedInboxes?.length ? (
                  renderDropdownValueItems(
                    selectedInboxes.map((inbox) => ({key: String(inbox.id), inbox})),
                    ({inbox}) => renderInboxValue(inbox),
                    {maxItems: 3},
                  )
                ) : (
                  <Trans id="rule-editor-data-table-inbox-list-cell-placeholder">Select inboxes</Trans>
                )}
              </StyledResourceLabelWrapper>
            )}
          </StyledBoxedInput>
        );
      }}
      selectedItems={inboxIds ?? null}
      onChange={(newSelectedInboxes) => {
        onChange(serializeDataFieldsTableResourceListValue(newSelectedInboxes?.filter(isNumber), 'inbox'));
      }}
    />
  );
};

const TeammateRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => {
  const teammateId = deserializeDataFieldsTableResourceValue(value, 'teammate');
  const {addSearchData} = useDataFieldsTableSearchContext();

  return (
    <TeammateParameter
      buttonWidth="100%"
      onFetchSelectedItems={(teammates) => addSearchData(teammates.map(teammateOrCircleToSearchValue))}
      renderButton={(selectedTeammate, isOpen) => (
        <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
          {() => (
            <StyledResourceLabelWrapper $isPlaceholder={!selectedTeammate}>
              {selectedTeammate && !isExpression(selectedTeammate) ? (
                renderTeammateValue(selectedTeammate)
              ) : (
                <Trans id="rule-editor-data-table-teammate-cell-placeholder">Select a teammate</Trans>
              )}
            </StyledResourceLabelWrapper>
          )}
        </StyledBoxedInput>
      )}
      selectedItem={teammateId ?? null}
      onChange={(newTeammateId) => {
        if (isExpressionRef(newTeammateId)) {
          return;
        }
        onChange(serializeDataFieldsTableResourceValue(newTeammateId, 'teammate'));
      }}
    />
  );
};

const TeammateListRenderer: FC<DataFieldsTableCellProps> = ({value, onChange}) => {
  const {addSearchData} = useDataFieldsTableSearchContext();
  const teammateAndCircleIds =
    value
      ?.split(dataFieldsTableListValueSeparator)
      .map((teammateOrCircleId) => {
        if (isCirclePublicId(teammateOrCircleId)) {
          return teammateOrCircleId;
        }
        return tryParsePublicId(teammateOrCircleId, 'teammate');
      })
      .filter(isDefined) ?? null;

  const {circles} = useFetchCircles(undefined);

  return (
    <TeammatesParameter
      buttonWidth="100%"
      onFetchSelectedItems={(teammates) => addSearchData(teammates.map(teammateOrCircleToSearchValue))}
      renderButton={(selectedTeammatesAndCirclesAndExpressions, isOpen) => {
        const selectedTeammatesAndCircles = selectedTeammatesAndCirclesAndExpressions?.filter(
          (teammateOrCircle): teammateOrCircle is CirclePublicId | TeammateModel =>
            !isExpression(teammateOrCircle),
        );

        return (
          <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL} hasHover={isOpen}>
            {() => (
              <StyledResourceLabelWrapper $isPlaceholder={!selectedTeammatesAndCircles?.length}>
                {selectedTeammatesAndCircles?.length ? (
                  renderDropdownValueItems(
                    selectedTeammatesAndCircles.map((teammateOrCircle) => ({
                      key: isCirclePublicId(teammateOrCircle)
                        ? teammateOrCircle
                        : String(teammateOrCircle.id),
                      teammateOrCircle,
                    })),
                    ({teammateOrCircle}) => renderTeammateOrCircleValue(teammateOrCircle, circles),
                    {maxItems: 3},
                  )
                ) : (
                  <Trans id="rule-editor-data-table-teammate-list-cell-placeholder">Select teammates</Trans>
                )}
              </StyledResourceLabelWrapper>
            )}
          </StyledBoxedInput>
        );
      }}
      selectedItems={teammateAndCircleIds}
      onChange={(newSelectedTeammates) => {
        const serializedValue = newSelectedTeammates
          ?.map((teammateOrCircle) => {
            if (isCirclePublicId(teammateOrCircle)) {
              return teammateOrCircle;
            }
            if (isNumber(teammateOrCircle)) {
              return serializeDataFieldsTableResourceValue(teammateOrCircle, 'teammate');
            }
            return undefined;
          })
          .filter(isDefined)
          .join(dataFieldsTableListValueSeparator);
        return onChange(serializedValue);
      }}
    />
  );
};

function inboxToSearchValue(inbox: InboxModel): DataFieldsTableSearchValue {
  return {publicId: buildPublicId('inbox', inbox.id), searchValue: inbox.name};
}

function renderInboxValue(inbox: InboxModel) {
  return (
    <StyledResourceItemWrapper>
      <UserThemeConsumer>
        {(theme) => (
          <Icon name="inboxSmall" color={inboxHasColor(inbox) ? inbox.color : theme.greys.shade30} />
        )}
      </UserThemeConsumer>
      <StyledResourceLabel>{inbox.name}</StyledResourceLabel>
    </StyledResourceItemWrapper>
  );
}

function teammateOrCircleToSearchValue(teammateOrCircle: TeammateModel | CircleModel) {
  if ('email' in teammateOrCircle) {
    return {
      publicId: buildPublicId('teammate', teammateOrCircle.id),
      searchValue: buildTeammateDisplayName(teammateOrCircle),
    };
  }
  return {publicId: buildPublicId('circle', teammateOrCircle.id), searchValue: teammateOrCircle.name};
}

function renderTeammateOrCircleValue(
  teammateOrCircle: TeammateModel | CirclePublicId,
  circles: ReadonlyArray<CircleModel>,
) {
  if (isCirclePublicId(teammateOrCircle)) {
    const privateId = tryParsePublicId(teammateOrCircle, 'circle');
    const circle = circles.find((c) => c.id === privateId);
    return (
      <StyledResourceItemWrapper>
        <Icon name="groupSmall" />
        <StyledResourceLabel>{circle?.name}</StyledResourceLabel>
      </StyledResourceItemWrapper>
    );
  }

  return renderTeammateValue(teammateOrCircle);
}

function renderTeammateValue(teammate: TeammateModel) {
  return (
    <StyledResourceItemWrapper>
      <TeammateAvatar teammate={teammate} size={VisualSizesEnum.SMALL} />
      <StyledResourceLabel>{buildTeammateDisplayName(teammate)}</StyledResourceLabel>
    </StyledResourceItemWrapper>
  );
}

function tagToSearchValue(tag: TagModel): DataFieldsTableSearchValue {
  return {publicId: buildPublicId('tag', tag.id), searchValue: tag.name};
}

function renderTagValue(tag: TagModel) {
  return (
    <StyledResourceItemWrapper key={tag.id}>
      {renderTagIcon(tag, false)}
      <StyledResourceLabel>{tag.name}</StyledResourceLabel>
    </StyledResourceItemWrapper>
  );
}

/*
 * Constants.
 */

const CELL_RENDERERS: Record<DataFieldsTableColumnTypesEnum, FC<DataFieldsTableCellProps>> = {
  [DataFieldsTableColumnTypesEnum.STRING]: TextRenderer,
  [DataFieldsTableColumnTypesEnum.TAG]: TagRenderer,
  [DataFieldsTableColumnTypesEnum.TAG_LIST]: TagListRenderer,
  [DataFieldsTableColumnTypesEnum.INBOX]: InboxRenderer,
  [DataFieldsTableColumnTypesEnum.INBOX_LIST]: InboxListRenderer,
  [DataFieldsTableColumnTypesEnum.TEAMMATE]: TeammateRenderer,
  [DataFieldsTableColumnTypesEnum.TEAMMATE_LIST]: TeammateListRenderer,
};
